import { X } from '@ui/components/icons';
import { Portal } from '@ui/lib/park/portal';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Link } from '@ui/lib/park/ui/link';
import { Skeleton } from '@ui/lib/park/ui/skeleton';
import { Text } from '@ui/lib/park/ui/text';
import { useEffect, useState } from 'react';
import { Flex, HStack, styled,VStack } from 'styled-system/jsx';

export type ConfirmTradeIntroVariant = 'USDC-LVUSD' | 'MON-LVMON';

const VARIANT_CONFIG: Record<
  ConfirmTradeIntroVariant,
  { imageSrc: string; learnMoreUrl: string }
> = {
  'USDC-LVUSD': {
    imageSrc: '/images/USDC-LVUSD.svg',
    learnMoreUrl: 'https://leverup.gitbook.io/docs/liquidity-layer/lvusd-stablecoin',
  },
  'MON-LVMON': {
    imageSrc: '/images/MON-LVMON.svg',
    learnMoreUrl: 'https://leverup.gitbook.io/docs/liquidity-layer/mon-lvmon',
  },
};

export const ConfirmTradeIntroModal = ({
  variant,
  onClose,
  onConfirm,
}: {
  variant: ConfirmTradeIntroVariant | null;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const isOpen = variant !== null;
  const cfg = variant ? VARIANT_CONFIG[variant] : null;
  const [isImgLoaded, setIsImgLoaded] = useState(false);

  useEffect(() => {
    setIsImgLoaded(false);
  }, [variant]);

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      modal={false}
      closeOnInteractOutside={false}
      open={isOpen}
      onOpenChange={(e) => {
        if (!e.open) onClose();
      }}
      type="desktopModal_mobileDrawer"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Title asChild>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                p="16px"
                borderBottom="1px solid"
                borderColor="v2.border.default"
              >
                <Text>Confirm Trade</Text>
                <Dialog.CloseTrigger boxShadow="none" bg="none">
                  <Icon w="20px" h="20px">
                    <X />
                  </Icon>
                </Dialog.CloseTrigger>
              </Flex>
            </Dialog.Title>

            <Dialog.Description
              p={4}
              pt={5}
              gap={4}
              display="flex"
              flexDir="column"
            >
              <Skeleton
                w="full"
                // keep layout stable while SVG loads
                aspectRatio={342 / 307}
                bg="v2.bg.surface1"
                borderRadius="4px"
                overflow="hidden"
                isLoaded={isImgLoaded}
              >
                {cfg && (
                  <styled.img
                    src={cfg.imageSrc}
                    alt={variant ?? 'confirm-trade'}
                    width="100%"
                    onLoad={() => setIsImgLoaded(true)}
                    onError={() => setIsImgLoaded(true)}
                    style={{
                      display: 'block',
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </Skeleton>

              <HStack gap={4} mt={2}>
                <Button
                  w="full"
                  size="mid"
                  variant="secondary"
                  asChild
                  onClick={() => {
                    // keep modal open; user can come back and confirm
                  }}
                >
                  <Link
                    href={cfg?.learnMoreUrl ?? '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    textDecoration="none"
                    _hover={{ textDecoration: 'none' }}
                  >
                    Learn more
                  </Link>
                </Button>

                <Button
                  w="full"
                  size="mid"
                  variant="primary"
                  onClick={onConfirm}
                >
                  Confirm
                </Button>
              </HStack>
            </Dialog.Description>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};


